<template>
  <form>
    <vl-properties-list>
      <vl-properties-label>{{ $t('deliveryentity.detail.id') }}</vl-properties-label>
      <vl-properties-data>{{ detail.id }}</vl-properties-data>
    </vl-properties-list>
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsInputField
        name="code"
        :label="t('deliveryentity.detail.code')"
        :placeholder="t('deliveryentity.detail.code')"
      />
    </vl-form-column>
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsInputField
        name="label"
        :label="t('deliveryentity.detail.label')"
        :placeholder="t('deliveryentity.detail.label')"
      />
    </vl-form-column>
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsTextAreaField
        name="description"
        :label="t('deliveryentity.detail.description')"
        :placeholder="t('deliveryentity.detail.description')"
      />
    </vl-form-column>
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsSelectField
        name="format"
        :label="t('deliveryentity.detail.format')"
        :placeholder="$t('deliveryentity.detail.format')"
        :options="formatTypes"
        :custom-label="formatTypeLabel"
      />
    </vl-form-column>
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsSelectField
        name="model"
        :label="t('deliveryentity.detail.model')"
        :placeholder="$t('deliveryentity.detail.model')"
        :options="entityModels"
        :custom-label="entityModelLabel"
        :sortFunction="entityModelSortFunction"
        :mod-loading="fetchingModels"
      />
    </vl-form-column>
    <vl-form-column v-if="entityModel && !(isCodeList || isTable)" v-vl-spacer:bottom.small >
      <PbsSelectField
        name="crs"
        :label="t('deliveryentity.detail.crs')"
        :placeholder="$t('deliveryentity.detail.crs')"
        :options="sridTypes"
        :custom-label="(e) => e.name"
        :mod-loading="fetchingModels"
      />
    </vl-form-column>
    <vl-form-column v-if="isCodeList" v-vl-spacer:bottom.small>
      <PbsSelectField
        name="dataConnection"
        :label="t('deliveryentity.detail.dataConnection')"
        :placeholder="$t('deliveryentity.detail.dataConnection')"
        :mod-loading="dataConnectionStore.loading"
        :options="dataConnections"
        :custom-label="(e) => e.code + ' (' + e.label + ')'"
      />
    </vl-form-column>
    <vl-form-column v-if="isCodeList" v-vl-spacer:bottom.small>
      <PbsInputField
        name="schema"
        :label="t('deliveryentity.detail.schema')"
        :placeholder="t('deliveryentity.detail.schema')"
      />
    </vl-form-column>
  </form>
</template>

<script lang="ts" setup>
import { computed, onMounted } from 'vue';
import { DeliveryEntityDetailDto, FormatTypeDto, SearchEntityModelDto, CrsDto, DataConnectionDto } from '@/api/backoffice/clients';
import { useForm } from 'vee-validate';
import { object, string, mixed } from 'yup';
import { useI18n } from 'vue-i18n';
import { useDeliveryEntityStore } from '@/modules/configuration/store/delivery-entity/delivery-entity.store';
import { useEntityModelSelectStore } from '@/modules/configuration/store/entity-model/entity-model-select.store';
import PbsSelectField from '@/components/forms/PbsSelectField.vue';
import PbsInputField from '@/components/forms/PbsInputField.vue';
import PbsTextAreaField from '@/components/forms/PbsTextAreaField.vue';
import { entityModelLabel, formatTypeLabel, entityModelSortFunction } from '@/modules/configuration/common';
import { RegularExpressions, noSurroundingSpaces } from '@/helpers';
import { ENUM_EntityTypes } from '@/modules/configuration/common';
import { useDataconnectionStore } from '@/modules/configuration/store/dataconnection/dataconnection.store';

const { t } = useI18n();
const store = useDeliveryEntityStore();
const entityModelSelectStore = useEntityModelSelectStore();
const dataConnectionStore = useDataconnectionStore();

const props = defineProps<{
  detail: DeliveryEntityDetailDto;
  specificationId: number;
}>();

const validationSchema = object({
  label: string()
    .required(t('validations.messages.required', { _field_: t('deliveryentity.detail.label') }))
    .test(noSurroundingSpaces(t('deliveryentity.detail.label')))
    .max(256, t('validations.messages.maxChar', { _field_: t('deliveryentity.detail.label'), length: 256 })),
  code: string()
    .required(t('validations.messages.required', { _field_: t('deliveryentity.detail.code') }))
    .max(64, t('validations.messages.maxChar', { _field_: t('deliveryentity.detail.code'), length: 64 }))
    .matches(
      RegularExpressions.noSpaces,
      t('validations.messages.noSpaces', { _field_: t('deliveryentity.detail.code') })
    ),
  description: string().required(
    t('validations.messages.required', { _field_: t('deliveryentity.detail.description') })
  ),
  format: object().required(t('validations.messages.required', { _field_: t('deliveryentity.detail.format') })),
  model: object().required(t('validations.messages.required', { _field_: t('deliveryentity.detail.model') })),
  schema: string()
    .matches(
      RegularExpressions.noSpaces,
      t('validations.messages.noSpaces', { _field_: t('deliveryentity.detail.schema') })
    ),
  custom: mixed().test(
            'custom-validation',
            'The properties do not meet the validation criteria',
            function (_, context) {
                const { crs
                } = context.parent;
                           
                if (!crs && !(isCodeList.value || isTable.value)) {
                  return context.createError({
                    path: 'crs',
                    message: t('validations.messages.required', {
                      _field_: t('deliveryentity.detail.crs')
                    })
                  });  
                }

                return true;
            }
        )
});

const { resetForm, handleSubmit, values } = useForm({
  validationSchema
});

const detail = computed((): DeliveryEntityDetailDto => {
  return props.detail;
});

const formatTypes = computed((): FormatTypeDto[] => {
  return store.formatTypes ?? [];
});

const sridTypes = computed((): CrsDto[] => {
  return store.sridTypes ?? [];
});

const fetchingModels = computed((): Boolean => {
  return entityModelSelectStore.loading;
});

const entityModels = computed((): SearchEntityModelDto[] => {
  return entityModelSelectStore.items ?? [];
});

const entityModel = computed(() => {
  return values.model;
});

const isTable = computed((): boolean => {
  return entityModel.value?.entityTypeName == ENUM_EntityTypes.Table.name;
});

const isCodeList = computed((): boolean => {
  return entityModel.value?.entityTypeName == ENUM_EntityTypes.CodeList.name;
});

const dataConnections = computed((): DataConnectionDto[] => {
  // For now filter on Operations dataconnection
  return (dataConnectionStore.items ?? []).filter((dc) => dc.id === 3);
});

onMounted(async () => {
  await entityModelSelectStore.getModels({ force: true, contextTypeName: 'Delivery' });
  await dataConnectionStore.get();

  resetForm({
    values: {
      label: detail.value.label,
      code: detail.value.code,
      description: detail.value.description,
      format: detail.value.format,
      crs: detail.value.crs,
      model: detail.value.model,
      schema: detail.value.schema,
      dataConnection: detail.value.dataConnection
    }
  });
});

const onSuccess = async (values) => {
  await store.update(props.specificationId, detail.value.id, {
    ...values,
    deliverySpecificationId: props.specificationId,
    modelId: values.model.id,
    format: values.format.code,
    crs: values.crs ? values.crs.name : 'Lambert72', // TODO: fix default crs
    schema: values.schema === "" ? null : values.schema,
    dataConnectionId: values.dataConnection?.id
  });
};

const onInvalidSubmit = ({ values, errors, results }) => {
  // console.log(values); // current form values
  // console.log(errors); // a map of field names and their first error message
  // console.log(results); // a detailed map of field names and their validation results
};

const onSubmit = handleSubmit(onSuccess, onInvalidSubmit);

defineExpose({ onSubmit });
</script>
